import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../utils/api';

function extractErrorMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (Array.isArray(data?.message)) return data.message.filter(Boolean).join(', ');
  if (typeof data === 'string' && data.trim()) return data;
  if (error?.response?.status) return `${fallback} (HTTP ${error.response.status}). Please try again.`;
  if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') return 'Cannot reach the server. Check your internet connection and try again.';
  return error?.message || fallback;
}

export interface User {
  id: string;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  role: string;
  domain: string;
  isActive: boolean;
  createdAt: string;
}

interface TwoFactorRequired {
  requires2FA: boolean;
  email: string;
  phoneHint: string;
  emailHint?: string;
  otpMethod?: 'sms' | 'email';
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  twoFactorRequired: TwoFactorRequired | null;
  
  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  clearTwoFactor: () => void;
  setUser: (user: User) => void;
  setAuth: (data: { user: Partial<User>; token: string; refreshToken?: string }) => void;
}

interface RegisterData {
  email: string;
  password: string;
  username: string;
  firstName?: string;
  lastName?: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      twoFactorRequired: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null, twoFactorRequired: null });
        try {
          const response = await api.post('/auth/user/login', { email, password });
          const data = response.data?.data || response.data;
          
          // Check if 2FA is required
          if (data.requires2FA) {
            set({
              twoFactorRequired: {
                requires2FA: true,
                email: data.email,
                phoneHint: data.phoneHint || '',
                emailHint: data.emailHint || '',
                otpMethod: data.otpMethod || 'sms',
              },
              isLoading: false,
            });
            return true; // Indicates 2FA is required
          }
          
          if (data.accessToken) {
            set({
              user: data.user,
              token: data.accessToken,
              isAuthenticated: true,
              isLoading: false,
            });
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('token', data.accessToken);
            if (data.refreshToken) {
              localStorage.setItem('refreshToken', data.refreshToken);
            }
            return false; // Indicates no 2FA, login complete
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error: any) {
          const message = extractErrorMessage(error, 'Login failed');
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      verifyOtp: async (email: string, otp: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/user/verify-otp', { email, otp, loginDomain: window.location.hostname });
          const data = response.data?.data || response.data;
          
          if (data.accessToken) {
            set({
              user: data.user,
              token: data.accessToken,
              isAuthenticated: true,
              isLoading: false,
              twoFactorRequired: null,
            });
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('token', data.accessToken);
            if (data.refreshToken) {
              localStorage.setItem('refreshToken', data.refreshToken);
            }
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error: any) {
          const message = extractErrorMessage(error, 'OTP verification failed');
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      clearTwoFactor: () => set({ twoFactorRequired: null, error: null }),

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/register', data);
          const result = response.data?.data || response.data;
          
          if (result.accessToken) {
            set({
              user: result.user,
              token: result.accessToken,
              isAuthenticated: true,
              isLoading: false,
            });
            localStorage.setItem('accessToken', result.accessToken);
            localStorage.setItem('token', result.accessToken);
            if (result.refreshToken) {
              localStorage.setItem('refreshToken', result.refreshToken);
            }
          }
        } catch (error: any) {
          const message = extractErrorMessage(error, 'Registration failed');
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        // Clear all persisted stores to prevent data leakage between users
        localStorage.removeItem('zmail-compose-tabs');
        localStorage.removeItem('zmail-settings');
        localStorage.removeItem('zmail-theme');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
      },

      refreshUser: async () => {
        const token = get().token || localStorage.getItem('accessToken');
        if (!token) return;

        try {
          const response = await api.get('/users/profile');
          const user = response.data?.data || response.data;
          set({ user, isAuthenticated: true });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),
      
      setUser: (user: User) => set({ user }),

      setAuth: (data: { user: Partial<User>; token: string; refreshToken?: string }) => {
        const user = {
          id: data.user.id || '',
          email: data.user.email || '',
          username: data.user.email?.split('@')[0] || '',
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          role: data.user.role || 'user',
          domain: data.user.email?.split('@')[1] || '',
          isActive: true,
          createdAt: new Date().toISOString(),
        } as User;

        localStorage.setItem('accessToken', data.token);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }

        set({
          user,
          token: data.token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
